import { fg } from '@atlaskit/platform-feature-flags';
import { type EntityATI } from '@atlassian/dynamic-cards-search-client';

import {
	allTypeFilterValueKeys,
	TypeFilterValueKey,
} from '../../../../../common/constants/filters';
import {
	products as productConfigs,
	type ProductKeys,
} from '../../../../../common/constants/products';
import { ContentType } from '../../../../../common/constants/schemas/query-params';
import { type OutboundAuthConfigsType } from '../../../../../common/types';
import { getTypeMapping } from '../../../../../common/utils/filters/constants';
import { type FetchedSelectFilterOption } from '../../../../filters/base-select-filter/types';
import { type CloudConfig } from '../../../../filters/types';

import { typeLabels } from './messages';

type FilterOption = FetchedSelectFilterOption & { products: ProductKeys[] };

const isProductEntitiesIncluded = (
	availableProducts: ProductKeys[],
	product: ProductKeys,
	outboundAuthConfigs: OutboundAuthConfigsType,
) => {
	if (!productConfigs[product]) {
		return false;
	} else if (productConfigs[product]?.is3P) {
		return availableProducts.includes(product) && !outboundAuthConfigs[product]?.userNeedsOAuth;
	} else {
		return availableProducts.includes(product);
	}
};

/**
 * Given the user's search input within the Type filter,
 * return the matching types to the user.
 */
export const lookupOptions = (query: string, config: CloudConfig): FetchedSelectFilterOption[] => {
	const allOptions = getAllTypeOptions(config);
	const matchingOptions = allOptions
		.filter((option) => lookupMatches(query, option.label))
		.map((option) => ({
			label: option.label,
			trackingKey: option.value,
			value: option.value,
			queryParamValue: option.queryParamValue,
			isDefault: option.isDefault,
		}));
	return matchingOptions;
};

/**
 * This function is used for populating the entities for the search request
 */
export const getEntitiesForQuery = (
	products: ProductKeys[],
	selectedTypes: TypeFilterValueKey[],
	outboundAuthConfigs: OutboundAuthConfigsType,
	availableProducts: ProductKeys[],
): {
	entitiesForQuery: EntityATI[];
	entitiesForResultsCountQuery: EntityATI[];
} => {
	if (fg('confluence_performance_improve_search_query')) {
		return getEntitiesForQueryPerformanceImproved({
			products,
			selectedTypes,
			outboundAuthConfigs,
			availableProducts,
		});
	}

	const selectedEntities = new Set<EntityATI>();
	const unselectedEntities = new Set<EntityATI>();
	const types = selectedTypes.length === 0 ? allTypeFilterValueKeys : selectedTypes;
	const typeMapping = getTypeMapping();

	const otherProducts = availableProducts.filter((product) => !products.includes(product));
	types.forEach((type) => {
		const productToEntityMap = typeMapping[type].entities;
		products.forEach((product) => {
			if (isProductEntitiesIncluded(products, product, outboundAuthConfigs)) {
				productToEntityMap[product]?.forEach((ati) => selectedEntities.add(ati));
			}
		});
		// collect unselected entities
		otherProducts.forEach((product) => {
			if (isProductEntitiesIncluded(otherProducts, product, outboundAuthConfigs)) {
				productToEntityMap[product]?.forEach((ati) => {
					if (!selectedEntities.has(ati)) {
						unselectedEntities.add(ati);
					}
				});
			}
		});
	});

	/// result count wants all entities for selected type
	const entitiesForResultsCountQuery = Array.from([
		...selectedEntities,
		...unselectedEntities,
	]).sort();

	// Do not show Atlas goal and tag results when no product is selected
	if (types.includes('project') && products.length > 1) {
		selectedEntities.delete('ati:cloud:townsquare:goal');
		selectedEntities.delete('ati:cloud:townsquare:tag');
	}

	// This exists to narrow down the possible types for Dynamic Cards
	// This reduces the entities we use for the query to just these two
	const dynamicCardsAllowedATIs: EntityATI[] = [
		'ati:cloud:confluence:page',
		'ati:cloud:confluence:blogpost',
	];

	const entitiesForQuery = Array.from(selectedEntities)
		.filter((entity) => {
			return dynamicCardsAllowedATIs.includes(entity);
		})
		.sort();

	// Sorting helps a ton in testing
	return {
		entitiesForQuery,
		entitiesForResultsCountQuery,
	};
};

const getProductsWithEntities = (
	products: ProductKeys[],
	outboundAuthConfigs: OutboundAuthConfigsType,
): ProductKeys[] => {
	return products.reduce<ProductKeys[]>((acc, product) => {
		if (isProductEntitiesIncluded(products, product, outboundAuthConfigs)) {
			acc.push(product);
		}

		return acc;
	}, []);
};

export const getEntitiesForQueryPerformanceImproved = ({
	products,
	selectedTypes,
	outboundAuthConfigs,
	availableProducts,
}: {
	availableProducts: ProductKeys[];
	outboundAuthConfigs: OutboundAuthConfigsType;
	products: ProductKeys[];
	selectedTypes: TypeFilterValueKey[];
}): {
	entitiesForQuery: EntityATI[];
	entitiesForResultsCountQuery: EntityATI[];
} => {
	const otherProducts = availableProducts.filter((product) => !products.includes(product));
	const productsWithEntities = getProductsWithEntities(products, outboundAuthConfigs);

	const otherProductsWithEntities = getProductsWithEntities(otherProducts, outboundAuthConfigs);

	// Stuff that we can't be cached that easy
	const selectedEntities = new Set<EntityATI>();
	const unselectedEntities = new Set<EntityATI>();
	const types = selectedTypes.length === 0 ? allTypeFilterValueKeys : selectedTypes;
	const typeMapping = getTypeMapping();

	// Using normal-loop to reduce overhead and improve performance
	for (let i = 0; i < types.length; i++) {
		const type = types[i];

		const productToEntityMap = typeMapping[type].entities;

		for (let h = 0; h < productsWithEntities.length; h++) {
			const product = productsWithEntities[h];
			const arr = productToEntityMap[product];

			if (!arr || !Array.isArray(arr)) {
				continue;
			}

			for (let w = 0; w < arr.length; w++) {
				const ati = arr[w];

				selectedEntities.add(ati);
			}
		}

		for (let j = 0; j < otherProductsWithEntities.length; j++) {
			const product = productsWithEntities[j];

			const arr = productToEntityMap[product];

			if (!arr || !Array.isArray(arr)) {
				continue;
			}

			for (let w = 0; w < arr.length; w++) {
				const ati = arr[w];
				if (!selectedEntities.has(ati)) {
					unselectedEntities.add(ati);
				}
			}
		}
	}
	/// result count wants all entities for selected type
	const entitiesForResultsCountQuery = Array.from([
		...selectedEntities,
		...unselectedEntities,
	]).sort();

	// Do not show Atlas goal and tag results when no product is selected
	if (types.includes('project') && products.length > 1) {
		selectedEntities.delete('ati:cloud:townsquare:goal');
		selectedEntities.delete('ati:cloud:townsquare:tag');
	}

	// This exists to narrow down the possible types for Dynamic Cards
	// This reduces the entities we use for the query to just these two
	const dynamicCardsAllowedATIs: EntityATI[] = [
		'ati:cloud:confluence:page',
		'ati:cloud:confluence:blogpost',
	];

	const entitiesForQuery = Array.from(selectedEntities)
		.filter((entity) => {
			return dynamicCardsAllowedATIs.includes(entity);
		})
		.sort();

	return {
		entitiesForQuery,
		entitiesForResultsCountQuery,
	};
};

export const lookupMatches = (query: string, label: string): boolean => {
	return label.toLowerCase().includes(query.toLowerCase());
};

export const getAllTypeOptions = (config: CloudConfig): FilterOption[] => {
	const typeMapping = getTypeMapping();

	const allOption: FilterOption = {
		products: [],
		label: config.intl?.formatMessage(typeLabels['all']) || '',
		value: 'all',
		trackingKey: 'all',
		queryParamValue: ContentType['All'],
		isDefault: true,
	};

	// This exists to narrow down the possible types for Dynamic Cards
	// This reduces the types we display in the filter to just these two
	const dynamicCardsAllowedTypes: TypeFilterValueKey[] = [
		TypeFilterValueKey.Page,
		TypeFilterValueKey.Blogpost,
	];

	const typeOptions: FilterOption[] = allTypeFilterValueKeys
		.filter((type) => {
			return dynamicCardsAllowedTypes.includes(type);
		})
		.map((type) => ({
			products: typeMapping[type].products,
			value: type,
			trackingKey: type,
			queryParamValue: type,
			isDefault: false,
			label: config.intl?.formatMessage(typeLabels[type]) || '',
		}));

	return [allOption, ...typeOptions];
};
